import { getServerSession, Session } from "next-auth";
import { JWT } from "next-auth/jwt";
import CredentialsProvider from "next-auth/providers/credentials";
import { signOut } from "next-auth/react";
import { ArbogenSession } from "@/utils/types/auth";

export const _getClientToken = async () => {
  const urlencoded = new URLSearchParams();
  urlencoded.append("grant_type", "client_credentials");
  urlencoded.append("client_id", process.env.KEYCLOAK_CLIENT_ID!);
  urlencoded.append("client_secret", process.env.KEYCLOAK_CLIENT_SECRET!);

  const resp = await fetch(
    `${process.env.KEYCLOAK_ISSUER}/protocol/openid-connect/token`,
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      method: "POST",
      body: urlencoded,
    },
  );
  const token = await resp.json();

  return {
    ...token,
    bearer: `Bearer ${token.access_token}`,
  };
};

async function refreshAccessToken(token: JWT): Promise<JWT | null> {
  if (!token.refreshToken) {
    console.error("No refresh token available");
    return null;
  }

  try {
    const params: URLSearchParams = new URLSearchParams();
    params.set("client_id", process.env.KEYCLOAK_CLIENT_ID!);
    params.set("client_secret", process.env.KEYCLOAK_CLIENT_SECRET!);
    params.set("grant_type", "refresh_token");
    params.set("refresh_token", token.refreshToken as string);

    const url = `${process.env.KEYCLOAK_ISSUER}/protocol/openid-connect/token`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error_description || "Refresh token request failed",
      );
    }

    const refreshedTokens = await response.json();

    return {
      ...token,
      accessToken: refreshedTokens.access_token,
      accessTokenExpires: Date.now() + refreshedTokens.expires_in * 1000,
      refreshToken: refreshedTokens.refresh_token ?? token.refreshToken,
    };
  } catch (error) {
    console.error("Error refreshing access token:", error);
    return null;
  }
}

export const decode = function (token: string) {
  try {
    if (!token || typeof token !== "string" || !token.includes(".")) {
      throw new Error("Invalid token format");
    }
    const parts = token.split(".");
    if (parts.length < 2) {
      throw new Error("Token does not have enough segments");
    }
    return JSON.parse(Buffer.from(parts[1], "base64").toString());
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

export const logOut = async (broadcastToOtherTabs: boolean = true) => {
  document.cookie.split(";").forEach(function (c) {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });

  // Logout in other tabs because user has logged out
  if (broadcastToOtherTabs) {
    try {
      const channel = new BroadcastChannel("auth-updates");
      channel.postMessage({
        type: "LOGOUT",
      });
      channel.close();
    } catch (broadcastError) {
      console.warn("Failed to broadcast logout event:", broadcastError);
    }
  }

  const contextParam = new URLSearchParams(window.location.search).get(
    "context",
  );
  const callbackUrl = contextParam
    ? `/login?context=${contextParam}`
    : "/login";

  signOut({ callbackUrl });
};

/**
 * Return the user UUID of the given session. Throw an error if the session is
 * invalid.
 *
 * Can be used in Server Actions to check if a user is authenticated.
 *
 * Should be used only server side.
 */
export const verifyServerSession = async (): Promise<{
  decodedToken: any;
  loggedInUuid: string;
}> => {
  const session = await getServerSession(authOptions);

  if (!session || !session.accessToken) {
    throw new Error("No access token.");
  }

  const decoded = decode(session.accessToken);

  if (!decoded || !decoded.sub) {
    throw new Error("Invalid access token.");
  }

  if (
    !session?.accessTokenExpires ||
    session.accessTokenExpires <= Date.now()
  ) {
    throw new Error("Invalid access token: expired or no expiration date.");
  }

  return { decodedToken: decoded, loggedInUuid: decoded.sub };
};

export const authOptions = {
  providers: [
    CredentialsProvider({
      name: "keycloakCustomProvider",
      credentials: {
        email: { label: "Username", type: "text" },
        password: { label: "Password", type: "password" },
      },
      async authorize(credentials, req) {
        const urlencoded = new URLSearchParams();
        urlencoded.append("grant_type", "password");
        urlencoded.append("client_id", "admin-cli");
        urlencoded.append("password", credentials?.password!);
        urlencoded.append("username", credentials?.email!);

        const resp = await fetch(
          `${process.env.KEYCLOAK_ISSUER}/protocol/openid-connect/token`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            method: "POST",
            body: urlencoded,
          },
        );

        if (resp.status === 200) {
          const token = await resp.json();
          return token;
        } else {
          return {
            message: resp.statusText,
          };
        }
      },
    }),
  ],
  pages: {
    signIn: "/login",
  },
  events: {
    async signOut(options: any) {
      const { token } = options;
      const logOutUrl = new URL(
        `${process.env.KEYCLOAK_ISSUER}/protocol/openid-connect/logout`,
      );
      logOutUrl.searchParams.set("id_token_hint", token.idToken);

      await fetch(logOutUrl);
    },
  },
  callbacks: {
    async jwt(params: { token: any; user: any }) {
      const { token, user } = params;
      if (user) {
        const decoded = decode(user.access_token);
        const newToken: JWT = {
          ...user,
          user: {
            firstName: decoded.given_name,
            lastName: decoded.family_name,
            email: decoded.email,
            id: decoded.sub,
            passwordPolicyCompliant: decoded.passwordPolicyCompliant,
          },
          roles: decoded.realm_access?.roles || [],
          idToken: decoded.sid,
          accessToken: user.access_token,
          accessTokenExpires: decoded?.exp ? decoded?.exp * 1000 : Date.now(),
          refreshToken: user.refresh_token,
        };
        return newToken;
      }
      // Return previous token if the access token has not expired yet
      if (Date.now() < token.accessTokenExpires) {
        return token;
      }

      // Access token has expired, try to update it
      return refreshAccessToken(token);
    },

    async session(params: any): Promise<ArbogenSession> {
      const { session, token } = params;
      session.user = token.user as Session["user"];
      session.roles = token.roles ?? [];
      session.accessToken = token.accessToken;
      session.accessTokenExpires = token.accessTokenExpires;
      session.error = token.error;
      session.user.id = token.user.id;

      return session;
    },
  },
};
